import { Navigate, useRoutes } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useMsal, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { fetchSystems } from './redux/slices/systemSlice';
import { fetchSites } from './redux/slices/sitesSlice';
import DashboardLayout from './layouts/dashboard';
import SimpleLayout from './layouts/simple';
import SiteStatusPage from './pages/SiteStatusPage';
import LoginPage from './pages/LoginPage';
import Page404 from './pages/Page404';
import ReportingPage from './pages/ReportingPage';
import DashboardAppPage from './pages/DashboardAppPage';
import { tokenRequest } from './authConfig';

// ----------------------------------------------------------------------

const UnauthenticatedRouter = () => {
  const unauthenticatedRoutes = useRoutes([
    { path: 'login', element: <LoginPage /> },
    { path: 'redirect', element: <Navigate to="/" /> },
    {
      element: <SimpleLayout />,
      children: [
        { element: <Navigate to="/login" />, index: true },
        { path: '404', element: <Page404 /> },
        { path: '*', element: <Navigate to="/404" /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
  return unauthenticatedRoutes;
};

const AuthenticatedRouter = ({ msSessionInfo, systemsList, sitesList }) => {
  const authenticatedRoutes = useRoutes([
    {
      path: '/dashboard',
      element: <DashboardLayout />,
      children: [
        { element: <Navigate to="/dashboard/home" />, index: true },
        { path: 'home', element: <DashboardAppPage msSessionInfo={msSessionInfo} /> },
        { path: 'sitestatus', element: <SiteStatusPage msSessionInfo={msSessionInfo} allSites={systemsList} /> },
        { path: 'sitestatus/:systemIds', element: <SiteStatusPage msSessionInfo={msSessionInfo} allSites={systemsList} /> },
        { path: 'reporting', element: <ReportingPage msSessionInfo={msSessionInfo} allSites={sitesList} /> },
      ],
    },
    { path: 'login', element: <LoginPage /> },
    { path: 'redirect', element: <Navigate to="/" /> },
    {
      element: <SimpleLayout />,
      children: [
        { element: <Navigate to="/dashboard/home" />, index: true },
        { path: '404', element: <Page404 /> },
        { path: '*', element: <Navigate to="/404" /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
  return authenticatedRoutes;
};

export default function Router() {
  const { instance } = useMsal();
  const [msSession, setMsSession] = useState(null);

  const hasFetchedSystems = useSelector((state) => state.systems.fetched);
  const systemData = useSelector((state) => state.systems.data);
  const hasFetchedSites = useSelector((state) => state.sites.fetched);
  const sitesData = useSelector((state) => state.sites.data);

  const dispatch = useDispatch();

  // ============= Fetch MSAL Token =============
  useEffect(() => {
    instance
      .acquireTokenSilent(tokenRequest)
      .then((response) => {
        const session = {
          userFullName: response.idTokenClaims.name,
          accessToken: response.accessToken,
        };
        setMsSession(session);
      })
      .catch(console.error);
  }, [instance]);

  // ============= Fetch Systems & Sites Data =============
  useEffect(() => {
    if (msSession && !hasFetchedSystems) {
      dispatch(fetchSystems(msSession));
    }
    if (msSession && !hasFetchedSites) {
      dispatch(fetchSites(msSession));
    }
  }, [msSession, hasFetchedSystems, hasFetchedSites, dispatch]);

  // ============= Prepare Lists =============
  const systemsList = React.useMemo(
    () =>
      systemData.map((system) => ({
        name: system.name,
        id: system.id,
        site_id: system.site_id,
      })),
    [systemData]
  );

  const sitesList = React.useMemo(
    () =>
      [...(sitesData ?? [])]
        .map((site) => ({
          name: site.name,
          id: site.id,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [sitesData]
  );

  return (
    <>
      <UnauthenticatedTemplate>
        <UnauthenticatedRouter />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        {msSession && (
          <AuthenticatedRouter
            msSessionInfo={msSession}
            systemsList={systemsList}
            sitesList={sitesList}
          />
        )}
      </AuthenticatedTemplate>
    </>
  );
}